import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Calendar, ArrowLeft, Search, Filter } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useBlogPosts, BlogPost } from "../hooks/useBlogPosts";
import { Link, useNavigate } from 'react-router-dom';

// Extended BlogPost interface with category
interface ExtendedBlogPost extends BlogPost {
  category?: string;
}

const truncate = (text: string, maxLength: number) => {
  if (!text) return '';
  return text.length > maxLength 
    ? text.substring(0, maxLength) + '...' 
    : text;
};

// strip HTML tags and decode entities using DOMParser (fallback to regex)
const stripHtml = (html?: string) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  } catch (e) {
    return html.replace(/<\/?[^>]+(>|$)/g, '');
  }
};

const formatDate = (timestamp: number | undefined | null) => {
  if (!timestamp || timestamp === 0 || isNaN(timestamp)) {
    return "Date unavailable";
  }
  
  try {
    const date = new Date(timestamp * 1000);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Date unavailable";
    }
    
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun", 
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return "Date unavailable";
  }
};

// Predefined mental health categories
const MENTAL_HEALTH_CATEGORIES = [
  'All Topics',
  'Anxiety & Depression',
  'Trauma & PTSD',
  'Relationships',
  'Self-Care',
  'Mindfulness',
  'Addiction & Recovery',
  'Family Therapy',
  'Child & Adolescent',
  'Grief & Loss',
  'Stress Management',
  'Professional Development'
];

const PreLoader = () => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
      <motion.div
        className="w-16 h-16 border-4 border-blue-500 rounded-full"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 360],
          borderRadius: ["20%", "50%", "20%"]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

const BlogListPage = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Topics');
  const navigate = useNavigate();
  const { posts } = useBlogPosts({ publishedOnly: true });

  // Simulate loading state
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // const publishedPosts = posts.filter((post: BlogPost) => post?.status === "published");
  // const displayedPosts = publishedPosts.length > 0 ? publishedPosts : posts;

  const displayedPosts = posts;

  // Add categories to posts based on content analysis
  const postsWithCategories: ExtendedBlogPost[] = displayedPosts.map((post: BlogPost) => {
    const content = (post.title + ' ' + post.content).toLowerCase();
    let category = 'General';
    
    // Simple content-based categorization
    if (content.includes('anxiety') || content.includes('depression') || content.includes('mood')) {
      category = 'Anxiety & Depression';
    } else if (content.includes('trauma') || content.includes('ptsd') || content.includes('abuse')) {
      category = 'Trauma & PTSD';
    } else if (content.includes('relationship') || content.includes('couple') || content.includes('marriage')) {
      category = 'Relationships';
    } else if (content.includes('self-care') || content.includes('wellness') || content.includes('health')) {
      category = 'Self-Care';
    } else if (content.includes('mindfulness') || content.includes('meditation') || content.includes('breathing')) {
      category = 'Mindfulness';
    } else if (content.includes('addiction') || content.includes('recovery') || content.includes('substance')) {
      category = 'Addiction & Recovery';
    } else if (content.includes('family') || content.includes('parenting')) {
      category = 'Family Therapy';
    } else if (content.includes('child') || content.includes('adolescent') || content.includes('teen')) {
      category = 'Child & Adolescent';
    } else if (content.includes('grief') || content.includes('loss') || content.includes('bereavement')) {
      category = 'Grief & Loss';
    } else if (content.includes('stress') || content.includes('work') || content.includes('pressure')) {
      category = 'Stress Management';
    } else if (content.includes('professional') || content.includes('career') || content.includes('development')) {
      category = 'Professional Development';
    }
    
    return {
      ...post,
      category
    };
  });

  // Filter posts based on search term and category
  const filteredPosts = postsWithCategories.filter((post: ExtendedBlogPost) => {
    const matchesSearch = post?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post?.content?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'All Topics' || 
                           post?.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Get unique categories from posts
  const availableCategories = ['All Topics', ...Array.from(new Set(postsWithCategories.map(post => post.category).filter(Boolean)))];

  if (isLoading) {
    return <PreLoader />;
  }

  if (!displayedPosts.length) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen bg-gray-50"
      >
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back</span>
            </button>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center bg-gray-50 p-4">
          <BookOpen className="w-24 h-24 text-blue-500 mb-6" />
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            No Blog Posts Found
          </h1>
          <p className="text-lg text-gray-600 max-w-md">
            We're curating some amazing content. Please check back later for updates.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Mental Health Blog | Expert Insights & Therapy Resources</title>
        <meta name="description" content="Discover expert mental health insights, therapy resources, and professional guidance. Read articles on anxiety, depression, relationships, trauma, and more from licensed therapists." />
        <meta name="keywords" content="mental health blog, therapy resources, anxiety help, depression support, trauma therapy, relationship counseling, mental wellness" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Mental Health Blog | Expert Insights & Therapy Resources" />
        <meta property="og:description" content="Discover expert mental health insights, therapy resources, and professional guidance from licensed therapists." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://therapistdirect.com/blog" />
        <link rel="canonical" href="https://therapistdirect.com/blog" />
        
        {/* Schema.org structured data for blog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "TherapistDirect Mental Health Blog",
            "description": "Expert mental health insights and therapy resources",
            "url": "https://therapistdirect.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "TherapistDirect",
              "url": "https://therapistdirect.com"
            },
            "blogPost": displayedPosts.map(post => {
              const createdAt = post.createdAt && !isNaN(post.createdAt) ? post.createdAt : Date.now() / 1000;
              const updatedAt = post.updatedAt && !isNaN(post.updatedAt) ? post.updatedAt : createdAt;
              
              return {
                "@type": "BlogPosting",
                "headline": post.title,
                "description": post.description || post.content.substring(0, 160),
                "author": {
                  "@type": "Person",
                  "name": post.author || "TherapistDirect Team"
                },
                "datePublished": new Date(createdAt * 1000).toISOString(),
                "dateModified": new Date(updatedAt * 1000).toISOString()
              };
            })
          })}
        </script>
      </Helmet>
      
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen bg-gray-50"
      >
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
          
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Mental Health Insights
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Empowering journeys through expert perspectives and compassionate guidance.
            </p>
          </div>

          {/* Search and Filter Section */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            
            {/* <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white min-w-[200px]"
              >
                {availableCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>  */}

          </div>        

          {/* Category Pills */}
          {/* <div className="flex flex-wrap gap-2 mb-4">
            {MENTAL_HEALTH_CATEGORIES.slice(1).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>  */}
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No posts found</h2>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {filteredPosts.map((post: ExtendedBlogPost, index: number) => (
                <motion.div
                  key={post?.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="block bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 h-full"
                    aria-label={`Read more about ${post?.title}`}
                  >
                    <article className="p-6 space-y-4 flex flex-col h-full">
                      <div className="flex-1">
                        <div className="mb-2">
                          <span className="inline-block px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                            {post?.category}
                          </span>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2 hover:text-blue-600 transition-colors">
                          {post?.title}
                        </h2>
                        <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                          {truncate(stripHtml(post?.content), 150)}
                        </p>
                      </div>
                      
                      <footer className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-blue-500" />
                          <time className="text-xs">
                            {formatDate(post?.published_at || undefined)}
                          </time>
                        </div>
                        <div className="text-xs text-gray-400">
                          {post?.author || 'Therapist Direct Team'}
                        </div>
                      </footer>
                    </article>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
    </>
  );
};

export default BlogListPage;